import { Slot } from '@radix-ui/react-slot';
import { CSSProperties, ElementType, forwardRef, HTMLAttributes, ReactNode, useMemo } from 'react';
import { PolymorphicComponentPropWithRef, PolymorphicRef } from '../../utils';
import type { FluidProp, ParseFluidOption } from './_fluid_prop';
import { parseFluid } from './_fluid_prop';
import { useLayoutContext } from './layout_provider/mod';

interface FlexStyles {
	/**
	 * Shorthand for `alignItems` style prop
	 *
	 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/align-items
	 */
	align?: CSSProperties['alignItems'];

	/**
	 * Shorthand for `flexBasis` style prop
	 *
	 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/flex-basis
	 */
	basis?: CSSProperties['flexBasis'];

	/**
	 * Shorthand for `flexDirection` style prop
	 *
	 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/flex-direction
	 */
	direction?: CSSProperties['flexDirection'];

	/**
	 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/gap
	 */
	gap?: CSSProperties['gap'];

	/**
	 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/flex-grow
	 */
	grow?: CSSProperties['flexGrow'];

	/**
	 * Shorthand for `justifyContent` style prop
	 *
	 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content
	 */
	justify?: CSSProperties['justifyContent'];

	/**
	 * Shorthand for `flexShrink` style prop
	 *
	 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/flex-shrink
	 */
	shrink?: CSSProperties['flexShrink'];

	/**
	 * Shorthand for `flexWrap` style prop
	 *
	 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/flex-wrap
	 */
	wrap?: CSSProperties['flexWrap'];

	/**
	 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/grid-area
	 */
	gridArea?: CSSProperties['gridArea'];
}

type FlexProps<C extends ElementType> = PolymorphicComponentPropWithRef<C, IntrinsicFlexProps>;

type IntrinsicFlexProps = FlexStyles &
	HTMLAttributes<HTMLDivElement> & {
		children?: ReactNode;

		/**
		 * Merges its props onto its immediate child.
		 */
		asChild?: boolean;

		/**
		 * Infers 100% width or height.
		 */
		fluid?: FluidProp;

		/**
		 * _NOTE: It's not type safe yet._
		 *
		 * Makes component behave polymorphic.
		 */
		breakpointSmallAndUp?: FlexStyles;
		breakpointMediumAndUp?: FlexStyles;
		breakpointLargeAndUp?: FlexStyles;
	};

function mergeStyles(
	{ align, direction, basis, gap, justify, shrink, wrap, grow, gridArea }: FlexStyles,
	rest: CSSProperties,
	fluid: FluidProp,
): Record<string, string | number | undefined> {
	const mergedStyles = {
		alignItems: align ?? rest?.alignItems,
		flexDirection: direction ?? rest?.flexDirection,
		flexBasis: basis ?? rest?.flexBasis,
		gap: gap ?? rest?.gap,
		justifyContent: justify ?? rest?.justifyContent,
		flexShrink: shrink ?? rest?.flexShrink,
		flexWrap: wrap ?? rest?.flexWrap,
		flexGrow: grow ?? rest?.flexGrow,
		gridArea: gridArea ?? rest?.gridArea,
		...rest,
	};

	return {
		...mergedStyles,
		...parseFluid({
			variant: fluid,
			direction: mergedStyles['flexDirection'] === 'column' ? 'vertical' : 'horizontal',
		} as ParseFluidOption),
	};
}

function CellulaFlex<C extends ElementType = 'div'>(
	{
		asChild,
		as,
		align,
		direction = 'column',
		basis,
		gap,
		grow,
		justify,
		shrink,
		wrap,
		gridArea,
		fluid,
		style: stylesBase,
		breakpointSmallAndUp: stylesS,
		breakpointMediumAndUp: stylesM,
		breakpointLargeAndUp: stylesL,
		className,
		...props
	}: FlexProps<C>,
	ref: PolymorphicRef<C>,
): JSX.Element {
	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const cssDomRenderer = useLayoutContext();

	const Comp = asChild ? Slot : (as ?? 'div');

	// biome-ignore lint/correctness/useHookAtTopLevel: suppressed due to migration - should be fixed
	const generatedClassName = useMemo(() => {
		return cssDomRenderer.convertStyleIntoClassName({
			base: {
				display: 'flex',
				...mergeStyles(
					{
						align,
						direction,
						basis,
						gap,
						grow,
						justify,
						shrink,
						wrap,
						gridArea,
					},
					stylesBase ?? {},
					fluid ?? false,
				),
			},
			S: stylesS ? mergeStyles(stylesS, {}, fluid ?? false) : undefined,
			M: stylesM ? mergeStyles(stylesM, {}, fluid ?? false) : undefined,
			L: stylesL ? mergeStyles(stylesL, {}, fluid ?? false) : undefined,
		});
	}, [
		cssDomRenderer,
		align,
		direction,
		basis,
		gap,
		grow,
		justify,
		shrink,
		wrap,
		gridArea,
		fluid,
		stylesBase,
		stylesS,
		stylesM,
		stylesL,
	]);
	className = Array.from(new Set([...(className?.split(' ') ?? []), ...generatedClassName])).join(' ');

	return <Comp className={className} {...props} ref={ref} />;
}

/**
 * `<Flex />` is a general purpose container with Cellula semantics that can be
 * used for custom layouts. It supports Cellula tokens to ensure
 * consistency with other Cellula components.
 */
export const Flex = forwardRef(
	CellulaFlex,
) as typeof CellulaFlex; /* This cast is necessary to unwrap the intrinsic types of the polymorphed component */
export type { FlexProps, IntrinsicFlexProps };
